import { TimeTrackingsRecordProvider } from '@enums/time-trackings-record-provider.enum';
import { DateHelper } from '@helpers/date.helper';
import { HttpHelper } from '@helpers/http.helper';
import { AccountingPeriod, AccountingPeriodJson } from '@models/accounting-period';
import { BusinessUser, BusinessUserJson } from '@models/business-user';
import { Company, CompanyJson } from '@models/company';
import { TimeTrackingsCategory, TimeTrackingsCategoryJson } from '@models/time-trackings-category';
import { TimeTrackingsDurationType, TimeTrackingsDurationTypeJson } from '@models/time-trackings-duration-type';
import { TimeTrackingsInvoicingType, TimeTrackingsInvoicingTypeJson } from '@models/time-trackings-invoicing-type';
import { TimeTrackingsTask, TimeTrackingsTaskJson } from '@models/time-trackings-task';

export interface TimeTrackingsRecordJson {
  id: number;
  date: string;
  company: Partial<CompanyJson>;
  accounting_period: Partial<AccountingPeriodJson>;
  wording: string;
  duration: number; // en minutes
  time_tracking_task: Partial<TimeTrackingsTaskJson>;
  time_tracking_category: Partial<TimeTrackingsCategoryJson>;
  time_tracking_invoicing_type: Partial<TimeTrackingsInvoicingTypeJson>;
  time_tracking_duration_type: Partial<TimeTrackingsDurationTypeJson>;
  validation_at: string;
  validation_by: Partial<BusinessUserJson>;
  provider: TimeTrackingsRecordProvider;
}

export class TimeTrackingsRecord {
  constructor(
    public id?: number,
    public date?: string,
    public company?: Company,
    public accountingPeriod?: AccountingPeriod,
    public wording?: string,
    public duration?: string,
    public timeTrackingTask?: TimeTrackingsTask,
    public timeTrackingCategory?: TimeTrackingsCategory,
    public timeTrackingInvoicingType?: TimeTrackingsInvoicingType,
    public timeTrackingDurationType?: TimeTrackingsDurationType,
    public validationAt?: Date,
    public validationBy?: BusinessUser,
    public provider?: TimeTrackingsRecordProvider
  ) {}

  static fromJson(json: TimeTrackingsRecordJson): TimeTrackingsRecord {
    return new TimeTrackingsRecord(
      json.id,
      json.date,
      json.company ? Company.fromJson(json.company) : null,
      json.accounting_period ? AccountingPeriod.fromJson(json.accounting_period) : null,
      json.wording,
      json.duration ? new Date(json.duration * 60000).toISOString().slice(11, 16) : null,
      json.time_tracking_task ? TimeTrackingsTask.fromJson(json.time_tracking_task) : null,
      json.time_tracking_category ? TimeTrackingsCategory.fromJson(json.time_tracking_category) : null,
      json.time_tracking_invoicing_type ? TimeTrackingsInvoicingType.fromJson(json.time_tracking_invoicing_type) : null,
      json.time_tracking_duration_type ? TimeTrackingsDurationType.fromJson(json.time_tracking_duration_type) : null,
      json.validation_at ? DateHelper.fromUtc(json.validation_at) : null,
      json.validation_by ? BusinessUser.fromJson(json.validation_by) : null,
      json.provider
    );
  }

  static toJson(record: TimeTrackingsRecord): Partial<TimeTrackingsRecordJson> {
    return {
      id: record.id || undefined,
      date: record.date || null,
      company: record.company?.id ? { id: record.company?.id } : null,
      accounting_period: record.accountingPeriod?.id ? { id: record.accountingPeriod?.id } : null,
      wording: HttpHelper.toJsonProperty(record.wording),
      duration: record.duration ? DateHelper.duration(record.duration).asMinutes() : null,
      time_tracking_task: record.timeTrackingTask?.id ? { id: record.timeTrackingTask?.id } : null,
      time_tracking_category: record.timeTrackingCategory?.id ? { id: record.timeTrackingCategory?.id } : null,
      time_tracking_invoicing_type: record.timeTrackingInvoicingType?.id
        ? { id: record.timeTrackingInvoicingType?.id }
        : null,
      time_tracking_duration_type: record.timeTrackingDurationType?.id
        ? { id: record.timeTrackingDurationType?.id }
        : null
    };
  }
}
